import { useCallback, useState } from "react";
import axios from "axios";
import type { ChangeRequestType } from "../../../api";
import { changeRequestsService } from "../../api_client";
import { showErrorToast, showSuccessToast } from "../../../utils/toasts";

interface SubmitArgs {
  type: ChangeRequestType;
  proposedChanges: Record<string, unknown>;
  targetBankAccountId?: string;
  targetProductId?: number;
  note?: string;
  /** An existing DRAFT for the same target: it's updated and submitted instead of creating a second request. */
  draftId?: string;
}

/**
 * Submits a change request as a single user-facing action: creates the draft,
 * then immediately transitions it to PENDING. The two-step backend flow
 * (DRAFT → submit) is an implementation detail the settings UI shouldn't
 * expose; from the seller's perspective they hit "Save" and the request
 * is on the admin's queue. When the user already has a saved draft for the
 * same target, that draft is reused — the backend allows one open request
 * per target, so creating another would be refused.
 */
export function useSubmitChangeRequest() {
  const [isLoading, setIsLoading] = useState(false);

  const submit = useCallback(async (args: SubmitArgs) => {
    setIsLoading(true);
    try {
      const draftId = args.draftId
        ? (
            await changeRequestsService.update(args.draftId, {
              proposedChanges: args.proposedChanges,
              note: args.note,
            })
          ).data.id
        : (
            await changeRequestsService.create({
              type: args.type,
              proposedChanges: args.proposedChanges,
              targetBankAccountId: args.targetBankAccountId,
              targetProductId: args.targetProductId,
              note: args.note,
            })
          ).data.id;
      const submitted = await changeRequestsService.submit(draftId);
      showSuccessToast("Submitted for review");
      return submitted.data;
    } catch (error) {
      const message = extractFriendlyMessage(error);
      showErrorToast(message, error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { submit, isLoading };
}

function extractFriendlyMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const raw = (error.response?.data as { message?: string } | undefined)?.message;
    if (raw && /open change request already exists/i.test(raw)) {
      return "A change request is already pending review. Wait for it to be approved or rejected before submitting another.";
    }
    if (raw) return raw;
  }
  return "Failed to submit change for review";
}
